/**
 * Components for displaying chat messages in different styles
 */

import { memo, useMemo } from 'react';
import { interleaveAsked } from '@inference.engine/agentic/human-input';
import { useChatBubble } from '../../model/useChatBubble';
import { ChatAskedQuestions } from '../ChatAskedQuestions';
import { ChatMarkdownRenderer } from '../ChatMarkdownRenderer';
import { MessageAttachments } from '../ChatMessage/MessageAttachments';
import { ErrorMetadata } from '../ErrorMetadata/ErrorMetadata';
import { AgentResponse } from './AgentResponse/AgentResponse';
import { AgentResponseSkeleton } from './AgentResponse/AgentResponseSkeleton';
import { ChatBubbleProps } from './ChatBubble.types';
import { UserBubble } from './UserBubble';
import { useTranslation } from 'react-i18next';

export const ChatBubble = memo(
  ({
    content,
    type,
    typewriter = true,
    agentId,
    messageId,
    hasWidget = false,
    files,
    isStreaming = false,
    metadata,
    steps,
    askedQuestions,
  }: ChatBubbleProps) => {
    const { t } = useTranslation('chats');

    const { renderingState, processedText } = useChatBubble({ content, type, agentId });

    // Memoize the disableAnimation calculation
    const disableAnimation = useMemo(() => {
      return hasWidget;
    }, [hasWidget]);

    // Each time the agent asked sits between what it said before and after, not in one block.
    const segments = useMemo(
      () =>
        askedQuestions ? interleaveAsked(processedText, askedQuestions) : [{ text: processedText }],
      [processedText, askedQuestions],
    );

    if (renderingState.shouldRenderUserBubble) {
      return <UserBubble content={content} files={files ?? []} metadata={metadata} />;
    }

    if (renderingState.shouldRenderSkeleton) {
      return <AgentResponseSkeleton />;
    }

    if (renderingState.shouldRenderError) {
      return (
        <AgentResponse agentId={agentId} messageId={messageId} hasWidget={hasWidget}>
          {steps}
          {segments.map((segment, index) =>
            'asked' in segment ? <ChatAskedQuestions key={index} items={segment.asked} /> : null,
          )}
          <span data-testid="agent-response-error">
            {t('chats.chatBubble.weAreUnableToProcess')}
          </span>
        </AgentResponse>
      );
    }

    return (
      <AgentResponse agentId={agentId} messageId={messageId} hasWidget={hasWidget}>
        {steps}
        {segments.map((segment, index) =>
          'asked' in segment ? (
            <ChatAskedQuestions key={index} items={segment.asked} />
          ) : (
            <ChatMarkdownRenderer
              key={index}
              content={segment.text}
              typewriter={typewriter}
              disableAnimation={disableAnimation}
              isStreaming={isStreaming && index === segments.length - 1}
            />
          ),
        )}
        <MessageAttachments files={files} type="assistant" agentId={agentId} />
        {metadata?.issue && <ErrorMetadata issue={metadata.issue} />}
      </AgentResponse>
    );
  },
);
ChatBubble.displayName = 'ChatBubble';
